<template>
  <aside class="flex flex-col gap-10 w-[16rem]">
    <div class="flex items-center min-h-14" v-if="menu">
      <t-heading branded tag="h3" class="font-semibold">{{ title }}</t-heading>
    </div>
    <!-- CONTENT -->
    <div
      ref="activity"
      @scroll="scroll($event)"
      class="flex flex-col gap-4 pb-10 overflow-x-hidden overflow-y-scroll scrollbar-hidden"
    >
      <div v-if="loader" class="flex flex-col h-full gap-4">
        <div v-for="idx in 7" :key="idx" class="flex items-center gap-4 cursor-pointer">
          <t-loader-skeleton
            :justifyContent="'flex-start'"
            :direction="'row'"
            :absolute="false"
            :padding="'0'"
            :w="'34px'"
            :h="'34px'"
            :br="'50%'"
          ></t-loader-skeleton>
          <div class="max-w-[12.5rem]">
            <div class="flex flex-col gap-1">
              <t-loader-skeleton
                :justifyContent="'flex-start'"
                :direction="'row'"
                :absolute="false"
                :padding="'0'"
                :w="'170px'"
                :h="'10px'"
              ></t-loader-skeleton>
              <t-loader-skeleton
                :justifyContent="'flex-start'"
                :direction="'row'"
                :absolute="false"
                :padding="'0'"
                :w="'140px'"
                :h="'10px'"
              ></t-loader-skeleton>
              <t-loader-skeleton
                :justifyContent="'flex-start'"
                :direction="'row'"
                style="margin-top: 5px"
                :absolute="false"
                :padding="'0'"
                :w="'90px'"
                :h="'7px'"
              ></t-loader-skeleton>
            </div>
          </div>
        </div>
      </div>
      <TransitionGroup v-show="!loader && userActivity.items.length > 0" name="activity" class="flex-col gap-2" style="display: flex">
        <div
          @click="openActivityItem(item)"
          v-for="item in userActivity.items"
          :key="item.id"
          class="flex items-center gap-4 cursor-pointer"
        >
          <img
            @click.stop="openUserProfile(item)"
            class="self-start rounded-full max-w-8 max-h-8"
            :src="item.click_url ? appLogo : Avatar(item.user)"
          />
          <div class="max-w-[12.5rem]">
            <div class="flex flex-col gap-1 text-black">
              <t-text size="md" class="break-word" :class="{'line-clamp-2': !item.click_url}">
                <div class="inline-flex gap-1">
                  <font>{{ item.user.fullname }}</font>
                  <img src="@/assets/static/icons/icon-verified__badge.svg" class="w-3 h-3 my-auto" v-if="item.user.is_verified" />
                </div>
                {{ actionWithoutName(item.action, item.user.fullname) }}
              </t-text>
              <t-text size="md" class="flex gap-1" color="gray">
                <font :data-time="item.timestamp" ref="activity_date">{{ $helpers.prettifyDate(item.timestamp) }}</font>
                <span v-if="!item.click_url">{{ $t('components.rightSidebar.in') }}</span>
                <font v-if="!item.click_url">{{ item.module.name }}</font>
              </t-text>
            </div>
          </div>
        </div>
      </TransitionGroup>
      <t-button
        @click.native="seeAll()"
        size="sm"
        border
        class="min-h-10"
        v-if="userActivity.items.length !== 0 && !loader && userActivity.totalPages > 1 && menu"
      >
        <t-text class="font-semibold" branded>{{ $t('activity.activitySeeAll') }}</t-text>
      </t-button>
      <div v-if="userActivity.items.length === 0 && !loader" class="flex-col gap-1" style="display: flex">
        <t-text class="font-semibold" color="black">{{ $t('activity.activityWebEmptyStateTitle') }}</t-text>
        <t-text size="sm" color="gray">{{ $t('activity.activityWebEmptyStateText') }}</t-text>
      </div>
    </div>
    <!-- CONTENT -->
  </aside>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  export default {
    name: 't-sidebar-right',
    data() {
      return {
        interval: null,
        title: '',
        loader: true,
        menu: false,
        scrollTop: 0
      }
    },
    computed: {
      ...mapGetters('user', ['userLoggedIn', 'userMenu', 'userActivity', 'activityLoaded', 'menuLoaded', 'userFirstModule']),
      ...mapGetters(['appLogo'])
    },
    async mounted() {
      await Promise.all([this.activityLoaded, this.menuLoaded])
      this.loader = false
      this.menu = true
      if (this.userActivity.scrollTop !== 0) this.$refs.activity.scrollTop = this.userActivity.scrollTop
      this.getActivityTitle()
      this.interval = setInterval(() => {
        this.updateActivitiesTime()
      }, 10000)
    },
    methods: {
      ...mapMutations('user', ['saveActivityState']),
      getActivityTitle() {
        this.title = this.userMenu.find(el => {
          return el.type === 'activity'
        }).name
      },
      scroll(e) {
        this.scrollTop = e.target.scrollTop
      },
      seeAll() {
        this.$router.push({
          name: 'activity.index',
          params: {
            app_module_id: this.userMenu.find(el => {
              return el.type === 'activity'
            }).app_module_id
          }
        })
      },
      Avatar(user) {
        return user.profile.find(el => {
          return el.name === 'avatar'
        }).value
      },
      updateActivitiesTime() {
        if (this.$refs.activity_date) {
          this.$refs.activity_date.forEach(date => {
            date.textContent = this.$helpers.prettifyDate(date.dataset.time)
          })
        }
      },
      actionWithoutName(action, name) {
        let reg = new RegExp(`${name}`)
        return action.replace(reg, '')
      },
      openUserProfile(activity) {
        this.$router.push({name: 'profile.other', params: {id: activity.user.id}})
      },
      openActivityItem(activity) {
        if (activity.click_url) {
          this.$router.push(activity.click_url.replace(/.*:\/\/[^/]+/, ''))
          return
        }
        const query = {}
        if (activity.is_reply || activity.is_comment) query.goTo = 'comments'
        if (activity.is_like) query.goTo = 'likes'
        if (activity.module.type === 'forums') {
          return this.$router.push({
            name: activity.module.type + '.item',
            params: {
              item_id: activity.entity_id,
              app_module_id: activity.module.app_module_id,
              discussion_name: activity.module.forums.title,
              discussion_id: activity.module.forums.id
            },
            query
          })
        }
        this.$router.push({
          name: activity.module.type + '.item',
          params: {
            id: activity.entity_id,
            app_module_id: activity.module.app_module_id
          },
          query
        })
      }
    },
    beforeDestroy() {
      this.saveActivityState(this.scrollTop)
      clearInterval(this.interval)
    }
  }
</script>

<style scoped>
  .activity__item {
    transition: all 0.5s;
  }
  .activity-enter,
  .activity-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }
  .activity-enter-to {
    opacity: 1;
    transform: translateX(0px);
  }

  .activity-move {
    opacity: 1;
    transition: all 0.5s;
  }
</style>
